import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, h } from "preact";
/** @import { TargetedMouseEvent } from "preact"; */

import { adminMemberResponse } from "~/backend/validate.js";
/** @import WS from "~/lib/ws/ws.js"; */
import { query, paginatedResponse } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import { SearchParams } from "~/lib/search.js";
import html from "~/util/html.js";
import { navigate, search } from "~/util/router.js";
/** @import { RouteParams } from "~/util/router.js"; */

import Button from "~/component/Button.js";
import Icon from "~/component/Icon.js";
import AssignedToFilter from "~/admin/component/AssignedToFilter.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import { ComboboxInput } from "~/admin/component/Combobox.js";
import MappingsLock from "~/admin/component/MappingsLock.js";
import Pagination from "~/admin/component/Pagination.js";
import MembersDetail from "./MembersDetail.js";
/** @import { orgResponse, sessionInfoResponse } from "~/backend/validate.js"; */
import adminStyle from "~/admin/lib/style.js";
import { changeRole } from "~/admin/res/member/actions/changeRole.js";
import { deleteMembers } from "~/admin/res/member/actions/deleteMembers.js";
import { manageGroups } from "~/admin/res/member/actions/manageGroups.js";
import MemberTable from "~/admin/res/member/MemberTable.js";
import util from "~/style/util.js";

/**
 * @extends {Component<Props>}
 *
 * @typedef {{
 * 	org: orgResponse;
 * 	enterprise: NonNullable<sessionInfoResponse["enterprise"]>;
 * 	ws: WS;
 * } & RouteParams<{ memberId: string | undefined }>} Props
 */
export default class MembersRoute extends Component {
  render() {
    const memberId = this.props.params.value.memberId;
    return [__h(Crumb, {}, memberId ? __h("a", {
      href: "/org/" + this.props.org.id + "/members"
    }, "Members") : "Members"), memberId ? __h(MembersDetail, {
      org: this.props.org,
      enterprise: this.props.enterprise,
      memberId: memberId,
      ws: this.props.ws
    }) : __h(Members, {
      org: this.props.org,
      enterprise: this.props.enterprise
    })];
  }
}

/**
 * @extends {Component<MembersProps>}
 *
 * @typedef {object} MembersProps
 * @property {orgResponse} org
 * @property {NonNullable<sessionInfoResponse["enterprise"]>} enterprise
 */
class Members extends Component {
  checked = signal(/** @type {Map<string, adminMemberResponse>} */new Map());
  paginate = new Pagination.State();
  sort = MemberTable.sort();
  members = query(() => ({
    key: [`/ui/admin/orgs/${this.props.org.id}/members`, {
      ...this.paginate.query(),
      ...this.sort.query(),
      q: SearchParams.q.value,
      group_id: search.value.group_id
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(adminMemberResponse)
  });
  render() {
    const orgId = this.props.org.id;
    const canEdit = identity.permission(orgId, "members", "edit") || identity.permission(orgId, "groups", "edit");
    return __h("div", {
      "class": adminStyle.main
    }, __h("div", {
      "class": adminStyle.search
    }, __h(AssignedToFilter, {
      org: this.props.org
    }), __h(ComboboxInput, {
      query: SearchParams.q,
      debounce: 200
    })), __h("div", {
      "class": adminStyle.body
    }, __h(MembersActions, {
      orgId: orgId,
      enterprise: this.props.enterprise,
      checked: this.checked
    }), __h("div", {
      "class": util.card + " " + adminStyle.tableCard
    }, h(MemberTable, {
      orgId,
      checked: canEdit ? this.checked : undefined,
      paginate: this.paginate,
      sort: this.sort,
      query: this.members,
      mappingsLock: this.props.enterprise.is_group_sync_enabled
    }))));
  }
}

/**
 * @extends {Component<MembersActionsProps>}
 *
 * @typedef {object} MembersActionsProps
 * @property {string} orgId
 * @property {NonNullable<sessionInfoResponse["enterprise"]>} enterprise
 * @property {typeof Members.prototype.checked} checked
 */
class MembersActions extends Component {
  render() {
    const checked = this.props.checked;
    const locked = this.props.enterprise.is_group_sync_enabled;
    const canInvite = identity.permission("enterprise", "members", "edit") && !this.props.enterprise.is_scim_enabled;
    const canEditMembers = identity.permission(this.props.orgId, "members", "edit");
    const canEditGroups = identity.permission(this.props.orgId, "groups", "edit");
    if (!canInvite && !canEditMembers && !canEditGroups) return null;
    return __h("menu", {
      "class": adminStyle.menu
    }, canInvite && __h(Button, {
      size: "small",
      onClick: () => navigate("/invites")
    }, __h(Icon, {
      name: "plus",
      size: 12
    }), __h("span", {}, "Invite members")), canEditMembers && __h(MappingsLock, {
      locked: locked
    }, __h(Button, {
      disabled: !(checked.value.size > 0) || locked,
      size: "small",
      onClick: (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => changeRole({
        anchor: event.currentTarget,
        placement: "bottom-start",
        orgId: this.props.orgId
      }, ...checked.value.values())
    }, __h(Icon, {
      name: "pencil",
      size: 12
    }), "Change role")), canEditGroups && __h(MappingsLock, {
      locked: locked
    }, __h(Button, {
      disabled: !(checked.value.size > 0) || locked,
      size: "small",
      onClick: async (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => {
        const res = await manageGroups({
          anchor: event.currentTarget,
          placement: "bottom-start",
          orgId: this.props.orgId
        }, ...checked.value.values());
        if (res) checked.value = new Map();
      }
    }, __h(Icon, {
      name: "users",
      size: 12
    }), "Manage groups")), canEditMembers && __h(MappingsLock, {
      locked: locked
    }, __h(Button, {
      disabled: !(checked.value.size > 0) || locked,
      size: "small",
      kind: "negative",
      onClick: async () => {
        const res = await deleteMembers({
          orgId: this.props.orgId
        }, ...checked.value.keys());
        if (res) checked.value = new Map();
      }
    }, __h(Icon, {
      name: "trash",
      size: 12
    }), "Remove")));
  }
}